import { DZ_COLOR_BLK_BLACK, DZ_COLOR_NEUTRAL_300, DZ_COLOR_PURPLE_200, DZ_COLOR_PURPLE_400 } from '@blk/daisy/core/styles';
import { CURRENCY_SYMBOLS } from '@ddt-web/common';
import { ColorsConfig } from '@ddt-web/config';
import { TabData } from '@ddt-web/data-service';
import { EsgTranslationUtil, NumberUtil, ObjectUtil, StringUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';
import * as reverse from 'lodash/reverse';
import * as size from 'lodash/size';

import { ChartFactory } from '../factories';
import { TooltipDispatcher } from '../tooltips';
import { ColumnChartUtil } from '../utils/column-chart.util';
import { ChartConfigHelper } from './config.helper';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';
import { ColumnChartName } from './enums/column-chart-name.enum';

declare const Highcharts;

const DEFAULT_COLUMN_CHART_CONFIG: any = {
	plotOptions: {
		column: {
			stacking: 'normal',
			dataLabels: {
				enabled: false
			},
			pointWidth: null
		},
		series: {
			borderWidth: 0
		}
	},
	tooltip: {
		headerFormat: '<b>{point.key}</b>',
		pointFormat: '{point.tooltip}'
	},
	xAxis: {
		labels: {
			enabled: true,
			style: {
				fontFamily: '"FortBold", Arial, sans-serif',
				fontSize: '14px',
				color: '#000',
				textOverflow: 'none'
			}
		},
		opposite: true,
		tickColor: '#fff'
	},
	yAxis: {
		title: {
			enabled: false
		},
		stackLabels: {
			enabled: false
		},
		labels: {
			formatter: function (): any {
				const label = this.axis.defaultLabelFormatter.call(this);
				const tickInterval = this.axis.tickInterval;
				if (!isNaN(label) && tickInterval % 1 !== 0) {
					return NumberUtil.formatNumber(Number(label), 1);
				}
				return label;
			}
		}
	}
};

export class ColumnChartConfig extends DefaultChartConfig {
	private seriesColors: Array<string> = [];
	private seriesData: Array<any> = [];
	private seriesDataTotal: Array<string> = [];
	private seriesCategories: Array<any> = [];

	constructor(
		protected type: ChartType,
		protected name: ColumnChartName,
		protected container: HTMLElement,
		protected data: any,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: ColumnChartCustomAttributes,
		protected tooltipType?: string
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_COLUMN_CHART_CONFIG);
		if (this.data instanceof TabData) {
			this.seriesColors = this.configuration.colors || ChartFactory.getDefaultColorsForTabDataReversed(this.data);
			if (this.name === ColumnChartName.GroupedColumnChart || (this.name === ColumnChartName.IqAnalyticsChart && !this.customAttributes.useTickerForIqAnalytics)) {
				this.seriesCategories = ChartFactory.getCategoriesForTabData(this.data, this.dataIndex);
				this.seriesData = ChartFactory.getDefaultColumnSeriesForTabData(this.data, true, null, this.dataIndex);
			} else if (this.name === ColumnChartName.IqUpsideDownsideChart || this.name === ColumnChartName.IqAnalyticsStacked) {
				this.seriesData = ChartFactory.getIqColumnSeriesForHeaderIds(
					this.data,
					this.name === ColumnChartName.IqUpsideDownsideChart,
					this.customAttributes?.customData?.tooltipData
				);
				this.seriesCategories = ChartFactory.getCategoriesForHeaderIds(this.data);
			} else if (this.name === ColumnChartName.PaCustomColumnChart) {
				this.seriesData = ChartFactory.getPaColumnSeriesForForHeaderIds(this.data);
				this.seriesCategories = ChartFactory.getCategoriesForHeaderIds(this.data);

			} else if (this.name === ColumnChartName.EsgUplift) {
				this.seriesData = ChartFactory.getDefaultColumnSeriesForTabData(this.data, true, this.customAttributes);
				this.seriesData[1].colors = ColorsConfig.ESG_UPLIFT_COLORS;
				this.seriesData[0].colors = ColorsConfig.ESG_UPLIFT_BENCHMARK_COLORS;
				this.seriesCategories = this.customAttributes.categories;
			} else {
				this.seriesCategories = ChartFactory.getPortfolioNamesForTabData(this.data);
				this.seriesData = ChartFactory.getDefaultColumnSeriesForTabData(this.data, false, this.customAttributes);
				this.seriesDataTotal = ChartFactory.getDefaultColumnTotalForTabData(this.data);
			}
		}
		if (this.data instanceof Array && this.name === ColumnChartName.GainLossChart) {
			const chartData = ColumnChartUtil.getCombinedTabDataForColumnChart(this.data);
			this.seriesColors = ChartFactory.getDefaultColorsForTabDataReversed(chartData);
			this.seriesData = ChartFactory.getDefaultColumnSeriesForTabData(chartData);
			this.seriesCategories = this.customAttributes.portfolioNames;
		}

		this.updateDefaultConfiguration();
		if (this.name === ColumnChartName.Risk) {
			this.initializeRiskChartConfiguration();
		} else if (this.name === ColumnChartName.NileRisk) {
			this.initializeNileRiskChartConfiguration(get(this.customAttributes, 'hasDataLabels', false));
		} else if (this.name === ColumnChartName.Exposure || this.name === ColumnChartName.NileExposure) {
			this.initializeExposureChartConfiguration();
		} else if (this.name === ColumnChartName.GroupedColumnChart) {
			this.initializeGroupedPerCharacteristicChartConfiguration();
		} else if (this.name === ColumnChartName.IqAnalyticsChart || this.name === ColumnChartName.IqAnalyticsStacked) {
			this.initializeGenericFundAnalyticsChartConfigurationWithTooltip(this.customAttributes?.usePercentTooltipForIq);
		} else if (this.name === ColumnChartName.IqUpsideDownsideChart || this.name === ColumnChartName.PaCustomColumnChart) {
			this.initializeGenericFundAnalyticsChartConfiguration();
		} else if (this.name === ColumnChartName.IretireAnnuityChart) {
			this.initializeIretireAnnuityChartConfiguration(this.customAttributes);
		} else if (this.name === ColumnChartName.IretireDecumHistogramChart) {
			this.initializeIretireDecumHistogramChart();
		} else if (this.name === ColumnChartName.EsgUplift) {
			this.initializeEsgUpliftChartConfig();
		}

		if (this.customAttributes) {
			const {
				portfolioNames,
				hasDataLabels,
				shouldReverseColors,
				contributionTooltipTabData,
				translateEsgValue,
				esgRatingsScaleMap,
				seriesLabelsFormatter,
				useTickerForIqAnalytics,
				customData,
				usePercentTooltipForIq,
				...validHighChartsAttributes
			} = this.customAttributes;
			this.configuration = ChartConfigHelper.applyCustomAttributesToConfiguration(validHighChartsAttributes, this.configuration);
			// chart categories are inserted reversely, hence we do the same for colors
			// we need however to select the same number of colors as categories before reversing
			if (shouldReverseColors && this.colors) {
				const nbCategories = size(get(this.data, 'tableData[0].headersY'));
				const exactColorsList =
					nbCategories > size(this.colors) ? this.colors.concat(Array(nbCategories - size(this.colors)).fill('#000')) : this.colors.slice(0, nbCategories);
				this.configuration.colors = reverse(exactColorsList);
			}
		}
	}

	public getConfigurationForPDF(customAttributes: any): any {
		let config = this.updateConfigurationForPDF();

		if (this.name === ColumnChartName.Risk) {
			config = this.getRiskChartConfigForPDF(config);
		} else if (this.name === ColumnChartName.NileRisk) {
			config = this.getNileRiskChartConfigForPDF(config, true);
		} else if (this.name === ColumnChartName.Exposure) {
			config = this.getExposureChartConfigForPDF(config);
		} else if (this.name === ColumnChartName.NileExposure) {
			config = this.getNileExposureChartConfigForPDF(config);
		} else if (this.name === ColumnChartName.GainLossChart) {
			config = ObjectUtil.extend(config, {
				chart: {
					marginLeft: 40,
					marginTop: 5
				},
				plotOptions: {
					column: {
						stacking: 'normal',
						dataLabels: {
							enabled: false
						},
						pointWidth: null
					},
					series: {
						borderWidth: 0
					}
				}
			});
		} else if (this.name === ColumnChartName.GroupedColumnChart) {
			config = this.getGroupedColumnChartConfigForPDF(config, customAttributes);
		} else if (this.name === ColumnChartName.IretireAnnuityChart) {
			this.initializeIretireAnnuityChartConfiguration(customAttributes, true); // flag for pdf
			config = this.configuration;
		} else if (this.name === ColumnChartName.IretireDecumHistogramChart) {
			config = this.initializeIretireDecumHistogramChart(true);
		} else if (this.name === ColumnChartName.EsgUplift) {
			config = this.initializeEsgUpliftChartConfigForPDF();
		} else {
			config = config;
		}

		return ChartConfigHelper.applyCustomAttributesToConfiguration(customAttributes, config);
	}

	private updateDefaultConfiguration(): void {
		let marginTop: number;
		let spacingBottom: number = 15;
		const sibling: any =
			this.container && this.container.parentElement && (this.container.parentElement.previousElementSibling || this.container.parentElement.nextElementSibling);
		if (sibling && sibling.tagName.toLowerCase() === 'dl-display-table') {
			marginTop = sibling.querySelector('header').offsetHeight + 1;
			spacingBottom = 10;
		}
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: marginTop,
				spacingBottom: spacingBottom
			},
			colors: this.seriesColors,
			series: this.seriesData,
			xAxis: {
				categories: this.seriesCategories
			}
		});
	}

	private updateConfigurationForPDF(): any {
		return ObjectUtil.extend(this.configuration, {
			chart: {
				marginLeft: 20,
				marginTop: 5
			},
			plotOptions: {
				column: {
					dataLabels: {
						enabled: true
					},
					pointWidth: get(this.seriesData[0], 'data.length', 0) === 3 ? 38 : undefined
				}
			},
			xAxis: {
				lineWidth: 0.25,
				lineColor: '#616161',
				labels: {
					enabled: false
				}
			},
			yAxis: {
				gridLineWidth: 0.25,
				gridLineColor: '#616161',
				labels: {
					style: { fontSize: '8px' }
				},
				offset: -10
			}
		});
	}

	private initializeRiskChartConfiguration(): void {
		const numberOfPortfolios = this.seriesDataTotal.length;

		// Setting up a custom 'dashed line' marker
		Highcharts.SVGRenderer.prototype.symbols.dashedLine = function (x, y, w): any {
			const returnArray = [];
			// set the start and end point of line (the calculation below should not be changed)
			const startPoint = x - (20 / numberOfPortfolios) * w;
			const endPoint = x + (16 / numberOfPortfolios) * w;
			const dashWidth = 0.5 * w;
			const gapWidth = 0.5 * w;
			const yValueWithOffset = y + w / 2; // offset by the radius of the marker to get the right position
			for (let currentPoint = startPoint; currentPoint <= endPoint; currentPoint += dashWidth + gapWidth /*jump forward one location*/) {
				returnArray.push('M', currentPoint, yValueWithOffset, 'L', currentPoint + dashWidth, yValueWithOffset);
			}
			returnArray.push('z'); // end drawing
			return returnArray;
		};

		const labelX = numberOfPortfolios === 2 ? -80 : -50 * (4 - numberOfPortfolios) + 5 * (1 - numberOfPortfolios);
		this.pushSeriesRiskConfiguration(labelX);

		if (get(this.customAttributes, 'contributionTooltipTabData')) {
			this.configuration.tooltip.headerFormat = null;
		}
	}

	private initializeNileRiskChartConfiguration(hasDataLabels: boolean = false): void {
		const numberOfPortfolios = this.seriesDataTotal.length;

		Highcharts.SVGRenderer.prototype.symbols.dashedLine = function (x, y, w, h): string[] {
			function getPointAdjustment(portfolioCount): number {
				switch (portfolioCount) {
					case 1:
						return 175;
					case 2:
						return 100;
					case 3:
						return 55;
					case 4:
						return 45;
					case 5:
						return 55;
					case 6:
					case 7:
						return 45;
				}
			}

			const returnArray = [];
			const startPoint = x - getPointAdjustment(numberOfPortfolios);
			const endPoint = x + getPointAdjustment(numberOfPortfolios);
			const dashWidth = w;
			const gapWidth = 0.5 * w;

			const yValWithOffset = y + w / 2; // account for marker radius when moving to the right Y position

			for (let currentPoint = startPoint; currentPoint <= endPoint; currentPoint += dashWidth + gapWidth /*jump forward one location*/) {
				returnArray.push('M', currentPoint, yValWithOffset, 'L', currentPoint + dashWidth, yValWithOffset);
			}
			returnArray.push('z'); // end drawing

			return hasDataLabels ? returnArray : [];
		};

		this.pushSeriesRiskConfiguration(0, hasDataLabels);
	}

	private pushSeriesRiskConfiguration(dataLabelXPos: number, hasDataLabels = true): void {
		// adding the median series with the marker config for dashedLine
		const seriesConfig = {
			id: 'markerSeries',
			type: ChartType.Scatter,
			data: new Array(this.seriesDataTotal.length).fill(0).map((p, i) => {
				// if the portfolio does not have any risk data, do not show the marker line
				if (parseFloat(this.seriesDataTotal[i]) !== 0) {
					return {
						y: parseFloat(this.seriesDataTotal[i]),
						color: ColorsConfig.CTA_COLOR
					};
				} else {
					return {
						visible: false
					};
				}
			}),
			showInLegend: false,
			enableMouseTracking: false,
			marker: {
				radius: 5,
				enabled: true,
				symbol: 'dashedLine',
				lineWidth: 1,
				lineColor: '#000'
			}
		};
		const esgTranslationMap: { [range: string]: string } = this.customAttributes?.esgRatingsScaleMap;
		const shouldTranslateEsgRatings = get(this.customAttributes, 'translateEsgValue', false);
		const seriesLabelsFormatter = this.customAttributes?.seriesLabelsFormatter;
		if (hasDataLabels) {
			seriesConfig['dataLabels'] = {
				overflow: 'none',
				crop: false,
				allowOverlap: true,
				enabled: true,
				x: dataLabelXPos,
				y: 3,
				padding: 5,
				formatter: function (): string {
					let formattedLabel = this.point.y.toFixed(2) + '%';
					if (shouldTranslateEsgRatings) {
						const translatedLabel = EsgTranslationUtil.getEsgLetterRating(esgTranslationMap, this.point.y);
						formattedLabel = `<span style="font-size: 14px">${translatedLabel}</span>`;
					} else if (seriesLabelsFormatter === '{y:0.2f}') {
						formattedLabel = this.point.y.toFixed(2);
					}
					return formattedLabel;
				},
				style: {
					fontFamily: '"FortBold", Arial, sans-serif',
					fontSize: '12px',
					color: '#000'
				}
			};
		}

		this.configuration.series.push(seriesConfig);
	}

	private initializeExposureChartConfiguration(): void {
		this.configuration.tooltip.headerFormat = null;
		this.configuration = ObjectUtil.extend(this.configuration, {
			colors: ChartFactory.getExposureColorsForTabData(this.data),
			plotOptions: {
				column: {
					stacking: 'percent',
					dataLabels: {
						align: 'center',
						enabled: true,
						useHTML: true,
						formatter: function (): string {
							const seriesNameWordsArray = this.series.name.split(' ');
							const isTooLessSpace = this.y < 10;
							const isTooMuchText = this.y < 20 && seriesNameWordsArray.length > 2;
							// check if label fits
							if (isTooLessSpace || isTooMuchText) {
								return null;
							} else {
								return seriesNameWordsArray.join('<br/>');
							}
						},
						style: {
							textOutline: 'none',
							fontSize: '12px'
						}
					}
				}
			},
			series: ChartFactory.getExposureColumnSeriesForTabData(this.data, this.customAttributes)
		});
	}

	private initializeGroupedPerCharacteristicChartConfiguration(): void {
		this.configuration = ObjectUtil.extend(this.configuration, {
			plotOptions: {
				column: {
					stacking: undefined
				}
			},
			colors: ColorsConfig.DEFAULT_SEQUENTIAL_COLORS_ORDERING,
			legend: {
				enabled: true,
				align: 'left',
				squareSymbol: true,
				verticalAlign: 'bottom',
				symbolRadius: 0,
				symbolHeight: 16,
				symbolWidth: 16,
				width: '100%',
				itemMarginTop: 16,
				itemDistance: 35,
				navigation: {
					enabled: false
				},
				itemStyle: {
					fontFamily: '"FortBook", Arial, sans-serif',
					fontSize: '14px',
					fontWeight: 'normal'
				}
			},
			chart: {
				height: 350,
				type: ChartType.Column,
				marginTop: 10
			},
			xAxis: {
				opposite: false,
				tickLength: 10,
				tickWidth: 1,
				tickmarkPlacement: 'on',
				tickColor: '#a5a5a5',
				lineColor: '#a5a5a5',
				labels: {
					enabled: true,
					style: { fontSize: '14px', fontFamily: '"FortBook", Arial, sans-serif', color: 'grey' }
				},
				title: {
					enabled: true,
					text: get(this.data, 'tableData[0].firstColumnTitle.formattedValue', ''),
					style: { fontSize: '14px', fontFamily: '"FortBook", Arial, sans-serif', color: 'black' }
				}
			},
			yAxis: {
				title: {
					enabled: true,
					text: get(this.data, 'tableData[0].firstColumnTitle.attribute.yLabel', ''),
					style: { fontSize: '14px', fontFamily: '"FortBook", Arial, sans-serif', color: 'black' }
				},
				plotLines: [
					{
						color: '#000',
						value: 0,
						width: 1,
						zIndex: 3
					}
				],
				gridLineColor: '#a5a5a5',
				startOnTick: true,
				endOnTick: true,
				labels: {
					enabled: true,
					style: { fontSize: '14px', fontFamily: '"FortBook", Arial, sans-serif', color: 'grey' },
					formatter: function (): string {
						const label = (this.value * 100).toFixed(1);
						if (label === '0.0') {
							return `<span style="color: black">${label}</span>`;
						}
						return label;
					}
				}
			},
			tooltip: {
				enabled: true,
				animation: true
			}
		});
	}

	private initializeGenericFundAnalyticsChartConfigurationWithTooltip(showPercent: boolean = true): void {
		this.initializeGenericFundAnalyticsChartConfiguration();
		this.configuration.tooltip.formatter = function (): string {
			return `
			<div style="width: 200px" class=column-chart-tooltip-container">
				<table style="width: 200px">
					<tr>
						<td><div class="text-12 text-bold">${StringUtil.escapeHtml(this.x)}</div></td>
						<td style="text-align:right">${StringUtil.escapeHtml(Highcharts.numberFormat(this.y, 2, '.', ','))}${showPercent ? '%' : ''}</td>
					</tr>
				</table>
			</div>`;
		};
	}

	private initializeGenericFundAnalyticsChartConfiguration(): void {
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: 40,
				height: 200
			},
			plotOptions: {
				column: {
					stacking: null,
					pointWidth: 24
				}
			},
			colors: [DZ_COLOR_PURPLE_400, DZ_COLOR_PURPLE_200],
			yAxis: {
				opposite: false,
				plotLines: [
					{
						value: 0,
						color: DZ_COLOR_BLK_BLACK,
						zIndex: 5
					}
				],
				title: {
					enabled: true,
					text: get(this.data, 'tableData[0].firstColumnTitle.attribute.yLabel', ''),
					style: { fontSize: '14px', fontFamily: '"FortBook", Arial, sans-serif', color: DZ_COLOR_BLK_BLACK },
					margin: 24
				},
				labels: {
					format: '{value:.1f}',
					formatter: null,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: '12px',
						color: DZ_COLOR_NEUTRAL_300
					}
				}
			},
			xAxis: {
				opposite: false,
				labels: {
					enabled: true,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: '12px',
						color: DZ_COLOR_NEUTRAL_300,
						lineHeight: '16px',
						textOverflow: 'none',
						whiteSpace: 'nowrap'
					},
					padding: 100
				},
				tickLength: 10,
				tickWidth: 1,
				tickmarkPlacement: 'on',
				tickColor: DZ_COLOR_NEUTRAL_300
			},
			tooltip: {
				enabled: true,
				backgroundColor: 'rgba(255, 255, 255, 0.9)',
				borderWidth: 1,
				padding: 12
			}
		});
	}

	private initializeIretireAnnuityChartConfiguration(customAttributes, isPdf: boolean = false): void {
		const legendNameMapping = customAttributes.legendNameMapping;
		const displayCurrency = customAttributes.displayCurrency || CURRENCY_SYMBOLS.USD;
		const multiplier = this.customAttributes.multiplier;
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: 20
			},
			xAxis: {
				labels: {
					style: {
						fontSize: isPdf ? '8px' : '11px',
						fontFamily: '"FortBook", Arial, sans-serif',
						color: 'grey'
					}
				},
				title: {
					text: 'Age',
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: isPdf ? '8px' : '14px'
					}
				},
				opposite: false
			},
			yAxis: {
				title: {
					enabled: true,
					text: customAttributes?.yTitle || '',
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: isPdf ? '8px' : '14px'
					}
				},
				labels: {
					formatter: function (): string {
						return displayCurrency + this.axis.defaultLabelFormatter.call(this);
					},
					style: { fontSize: isPdf ? '8px' : '11px' }
				}
			},
			legend: {
				enabled: true,
				itemStyle: { fontSize: isPdf ? '8px' : '14px', fontWeight: 'normal' },
				labelFormatter: function (): string {
					return legendNameMapping[this.name] || this.name;
				},
				itemMarginBottom: 4,
				symbolRadius: 0,
				align: 'center',
				reversed: true,
				symbolPadding: 4,
				y: 8
			},
			tooltip: {
				backgroundColor: 'rgba(255, 255, 255, 0.9)',
				borderWidth: 1,
				formatter: new TooltipDispatcher(this.type, this.tooltipType).getTooltipFormatter({
					multiplier: multiplier,
					displayCurrency: displayCurrency,
					customData: this.customAttributes.customData
				})
			}
		});
	}

	private initializeEsgUpliftChartConfig() {
		const esgTranslationMap: { [range: string]: string } = this.customAttributes?.esgRatingsScaleMap;
		this.configuration = ObjectUtil.extend(this.configuration, {
			xAxis: {
				opposite: false,
				labels: {
					enabled: true,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: '12px',
						color: DZ_COLOR_NEUTRAL_300,
						lineHeight: '16px',
						textOverflow: 'none',
						whiteSpace: 'nowrap'
					}
				}
			},
			plotOptions: {
				column: {
					stacking: null,
					dataLabels: {
						overflow: 'none',
						crop: false,
						allowOverlap: true,
						enabled: true,
						x: 0,
						y: 3,
						padding: 5,
						formatter: function (): string {
							const translatedLabel = EsgTranslationUtil.getEsgLetterRating(esgTranslationMap, this.point.y);
							const formattedLabel = `<span style="font-size: 14px">${translatedLabel}</span>`;
							return formattedLabel;
						},
						style: {
							fontFamily: '"FortBold", Arial, sans-serif',
							fontSize: '12px',
							color: '#000'
						}
					}
				}
			}
		});
		return this.configuration;
	}

	private initializeEsgUpliftChartConfigForPDF() {
		const esgTranslationMap: { [range: string]: string } = this.customAttributes?.esgRatingsScaleMap;
		this.configuration = ObjectUtil.extend(this.configuration, {
			xAxis: {
				opposite: false,
				labels: {
					enabled: true,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: '9px',
						color: DZ_COLOR_NEUTRAL_300,
						lineHeight: '16px',
						textOverflow: 'none',
						whiteSpace: 'nowrap'
					}
				}
			},
			plotOptions: {
				column: {
					stacking: null,
					dataLabels: {
						overflow: 'none',
						crop: false,
						allowOverlap: true,
						enabled: true,
						x: 0,
						y: 3,
						padding: 5,
						formatter: function (): string {
							const translatedLabel = EsgTranslationUtil.getEsgLetterRating(esgTranslationMap, this.point.y);
							const formattedLabel = `<span style="font-size: 10px">${translatedLabel}</span>`;
							return formattedLabel;
						},
						style: {
							fontFamily: '"FortBold", Arial, sans-serif',
							fontSize: '12px',
							color: '#000'
						}
					}
				}
			}
		});
		return this.configuration;
	}

	private initializeIretireDecumHistogramChart(isPdf: boolean = false): any {
		const multiplier = this.customAttributes.multiplier;
		const displayCurrency = this.customAttributes.displayCurrency || CURRENCY_SYMBOLS.USD;
		const callbackFunctions = this.customAttributes.callbackFunctions;
		this.configuration = ObjectUtil.extend(this.configuration, {
			chart: {
				type: 'column',
				marginTop: 5,
				spacingBottom: 0,
				events: {
					load: function (): void {
						this.series.forEach(function (s): void {
							s.update({
								showInLegend: s.points.length
							});
						});
					}
				}
			},

			xAxis: {
				opposite: false,
				categories: this.data.age,
				title: {
					text: this.customAttributes?.customData?.xAxisTitle,
					style: { fontSize: isPdf ? '8px' : '14px' }
				},
				labels: {
					style: {
						textOverflow: 'none',
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: isPdf ? '8px' : '12px',
						color: 'grey'
					}
				}
			},
			yAxis: {
				min: 0,
				title: {
					enabled: true,
					text: this.customAttributes?.customData?.yAxisTitle,
					style: { fontSize: isPdf ? '8px' : '14px' }
				},
				labels: {
					style: {
						textOverflow: 'none',
						fontSize: isPdf ? '8px' : '12px'
					}
				}
			},
			legend: {
				enabled: true,
				itemStyle: { fontSize: isPdf ? '8px' : '14px', fontWeight: 'normal' },
				itemMarginBottom: 4,
				itemMarginRight: 20,
				symbolRadius: 0,
				align: 'center',
				reversed: true
			},
			tooltip: {
				shared: true,
				shadow: true,
				backgroundColor: 'rgba(255, 255, 255, 0.9)',
				borderWidth: 1,
				useHTML: true,
				formatter: new TooltipDispatcher(this.type, this.tooltipType).getTooltipFormatter({
					multiplier: multiplier,
					displayCurrency: displayCurrency,
					customData: this.data.tooltipData
				})
			},
			plotOptions: {
				series: {
					states: {
						inactive: {
							opacity: 0.5
						}
					},
					point: {
						events: {
							click: function (): void {
								if (this.category === 85) {
									callbackFunctions?.toggleMessageForAgeEqual85();
								} else {
									callbackFunctions?.toggleMessageForAgeOtherThan85('column');
								}
							}
						}
					}
				},
				column: {
					stacking: 'normal',
					dataLabels: {
						enabled: false
					}
				}
			},
			series: this.data.seriesData
		});

		return this.configuration;
	}

	private getRiskChartConfigForPDF(config: any): any {
		const numberOfPortfolios = this.seriesDataTotal.length;
		const labelX = numberOfPortfolios === 1 ? -75 : -30 - (3 - numberOfPortfolios) * 15;

		// find the markerSeries and hide the data label for it in PDF
		const seriesPdf = this.seriesData.map(seriesObj => {
			if (seriesObj.id === 'markerSeries') {
				seriesObj.dataLabels = { enabled: false };
			}
			return seriesObj;
		});

		// Setting up a custom 'dashed line' marker for PDF
		Highcharts.SVGRenderer.prototype.symbols.dashedLine = function (x, y, w): any {
			const returnArray = [];
			// set the start and end point of line (the calculation below should not be changed)
			const startPoint = x - (7 / numberOfPortfolios) * w - 4 * numberOfPortfolios;
			const endPoint = x + (7 / numberOfPortfolios) * w + 4 * numberOfPortfolios;
			const dashWidth = 0.3 * w;
			const gapWidth = 0.3 * w;
			const yValueWithOffset = y + w / 2; // offset by the radius of the marker to get the right position
			for (let currentPoint = startPoint; currentPoint <= endPoint; currentPoint += dashWidth + gapWidth /*jump forward one location*/) {
				returnArray.push('M', currentPoint, yValueWithOffset, 'L', currentPoint + dashWidth, yValueWithOffset);
			}
			returnArray.push('z'); // end drawing
			return returnArray;
		};

		const that = this;
		// add data label for the first column in the stack with the total risk value
		return ObjectUtil.extend(config, {
			plotOptions: {
				column: {
					dataLabels: {
						verticalAlign: 'top',
						x: labelX,
						y: -16,
						overflow: 'allow',
						crop: false,
						formatter: function (): string {
							if (this.point.firstColumnStack) {
								return that.seriesDataTotal[this.point.index];
							}
							return;
						},
						style: {
							fontFamily: '"FortBold", Arial, sans-serif',
							fontSize: '8px',
							color: '#000'
						}
					},
					// setting the width for column to 30 only when there are 3 portfolios to analyse - this is needed for the marker line to fit
					pointWidth: get(this.seriesData[0], 'data.length', 0) === 3 ? 30 : undefined
				}
			},
			// add the series with no data label for markerSeries
			series: seriesPdf
		});
	}

	private getNileRiskChartConfigForPDF(config: any, hasDataLabels: boolean): any {
		const numberOfPortfolios = this.seriesDataTotal.length;

		Highcharts.SVGRenderer.prototype.symbols.dashedLine = function (x, y, w, h): string[] {
			function getPointAdjustment(portfolioCount): number {
				switch (portfolioCount) {
					case 1:
						return 115;
					case 2:
						return 60;
					case 3:
						return 40;
					case 4:
						return 30;
					case 5:
						return 25;
					case 6:
					case 7:
						return 15;
				}
			}

			const returnArray = [];
			const startPoint = x - getPointAdjustment(numberOfPortfolios);
			const endPoint = x + getPointAdjustment(numberOfPortfolios);
			const dashWidth = w;
			const gapWidth = 0.5 * w;

			const yValWithOffset = y + w / 2; // account for marker radius when moving to the right Y position

			for (let currentPoint = startPoint; currentPoint <= endPoint; currentPoint += dashWidth + gapWidth /*jump forward one location*/) {
				returnArray.push('M', currentPoint, yValWithOffset, 'L', currentPoint + dashWidth, yValWithOffset);
			}
			returnArray.push('z'); // end drawing

			return hasDataLabels ? returnArray : [];
		};
		const that = this;
		// add data label for the first column in the stack with the total risk value
		return ObjectUtil.extend(config, {
			chart: {
				marginLeft: 26
			},
			yAxis: {
				offset: 0,
				labels: {
					y: 3,
					x: 0,
					formatter: ColumnChartUtil.yAxisLabelPercentageFormatter,
					style: {
						fontSize: '7px'
					}
				},
				plotLines: [
					{
						value: 0,
						color: 'black',
						width: 0.3,
						dashStyle: 'solid',
						zIndex: 10
					}
				],
				title: {
					enabled: true,
					text:
						get(this.data, 'tableData[0].firstColumnTitle.attribute.yLabel.defaultText') ||
						get(this.data, 'config.extractorConfig.firstColumnTitle.attributes.yLabel.defaultText', ''),
					margin: 0,
					offset: 21,
					style: {
						fontSize: '7px',
						fontFamily: '"FortBook", Arial, sans-serif',
						color: '#666666',
						width: '200px'
					}
				}
			},
			plotOptions: {
				column: {
					dataLabels: {
						verticalAlign: 'top',
						x: 0,
						y: -16.5,
						overflow: 'allow',
						crop: false,
						formatter: function (): string {
							if (this.point.firstColumnStack) {
								return that.seriesDataTotal[this.point.index];
							}
							return;
						},
						style: {
							fontFamily: '"Fort", Arial, sans-serif',
							fontSize: '7.5px',
							color: '#000'
						}
					}
				}
			}
		});
	}

	private getExposureChartConfigForPDF(config: any): any {
		return ObjectUtil.extend(config, {
			plotOptions: {
				column: {
					dataLabels: {
						formatter: function (): string {
							const seriesNameWordsArray = this.series.name.split(' ');
							const isTooLessSpace = this.y < 10;
							const isTooMuchText = this.y < 25 && seriesNameWordsArray.length > 2;
							// check if label fits
							if (isTooLessSpace || isTooMuchText) {
								return null;
							} else {
								return seriesNameWordsArray.join('<br/>');
							}
						},
						style: {
							fontSize: '6px',
							lineHeight: '9px'
						}
					}
				}
			},
			yAxis: {
				tickInterval: 10
			}
		});
	}

	private getNileExposureChartConfigForPDF(config: any): any {
		return ObjectUtil.extend(config, {
			chart: {
				marginLeft: 26
			},
			plotOptions: {
				column: {
					dataLabels: {
						formatter: function (): string {
							const seriesNameWordsArray = this.series.name.split(' ');
							const isTooLessSpace = this.y < 10;
							const isTooMuchText = this.y < 25 && seriesNameWordsArray.length > 2;
							// check if label fits
							if (isTooLessSpace || isTooMuchText) {
								return null;
							} else {
								return seriesNameWordsArray.join('<br/>');
							}
						},
						style: {
							fontSize: '6px',
							lineHeight: '9px'
						}
					}
				}
			},
			yAxis: {
				offset: 0,
				tickInterval: null,
				showLastLabel: true,
				title: {
					enabled: true,
					text:
						get(this.data, 'tableData[0].firstColumnTitle.attribute.yLabel.defaultText') ||
						get(this.data, 'config.extractorConfig.firstColumnTitle.attributes.yLabel.defaultText', ''),
					margin: 0,
					offset: 21,
					style: {
						fontSize: '7px',
						fontFamily: '"FortBook", Arial, sans-serif',
						color: '#666666',
						width: '200px'
					}
				},
				labels: {
					y: 3,
					x: 0,
					style: {
						fontSize: '7px',
						textOverflow: 'none'
					},
					overflow: 'allow'
				},
				plotLines: [
					{
						value: 0,
						color: 'black',
						width: 0.3,
						dashStyle: 'solid',
						zIndex: 10
					}
				]
			}
		});
	}

	private getGroupedColumnChartConfigForPDF(config: any, customAttributes: any): any {
		return ObjectUtil.extend(config, {
			plotOptions: {
				column: {
					pointWidth: null,
					dataLabels: {
						enabled: false
					}
				}
			},
			legend: {
				enabled: true,
				align: 'left',
				squareSymbol: true,
				verticalAlign: 'bottom',
				symbolPadding: 2,
				symbolRadius: 0,
				symbolHeight: 8,
				symbolWidth: 8,
				width: '50%',
				itemMarginTop: 2,
				itemMarginBottom: 2,
				itemDistance: 5,
				navigation: {
					enabled: false
				},
				itemStyle: {
					fontFamily: '"FortBook", Arial, sans-serif',
					fontSize: '8px',
					fontWeight: 'normal'
				}
			},
			yAxis: {
				offset: -10,
				showLastLabel: true,
				plotLines: [
					{
						value: 0,
						color: 'black',
						width: 0.3,
						dashStyle: 'solid',
						zIndex: 10
					}
				],
				title: {
					enabled: true,
					reserveSpace: true,
					style: {
						fontSize: '8px',
						textOverflow: 'none',
						fontFamily: '"FortBook", Arial, sans-serif',
						color: 'grey'
					}
				},
				labels: {
					style: {
						fontSize: '8px',
						textOverflow: 'none',
						fontFamily: '"FortBook", Arial, sans-serif',
						color: 'grey'
					}
				}
			},
			xAxis: {
				opposite: false,
				tickLength: 10,
				tickWidth: 1,
				tickmarkPlacement: 'on',
				tickColor: '#a5a5a5',
				lineColor: '#a5a5a5',
				labels: {
					enabled: true,
					style: { fontSize: '8px', fontFamily: '"FortBook", Arial, sans-serif', color: 'grey' }
				},
				title: {
					enabled: false
				}
			},
			chart: {
				height: 200,
				marginLeft: 50
			}
		});
	}

	public render(): void {
		Highcharts.chart(this.configuration);
	}
}

export interface ColumnChartCustomAttributes {
	portfolioNames?: any[];
	hasDataLabels?: boolean;
	shouldReverseColors?: boolean;
	contributionTooltipTabData?: TabData;
	translateEsgValue?: boolean;
	esgRatingsScaleMap?: { [range: string]: string };
	seriesLabelsFormatter?: string;
	useTickerForIqAnalytics?: boolean;
	customData?: { [key: string]: any };
	callbackFunctions?: { [key: string]: (args?: any) => void };
	displayCurrency?: string;
	multiplier?: number;
	usePercentTooltipForIq?: boolean;
	categories?: any;
}
